/**
 * @license
 * @copyright Copyright Motili Inc., 2024 All Rights Reserved
 */

import { Component } from 'react';
import FormLabel from 'common/components/form/FormLabel';
import { find as _find, findIndex, sortBy } from 'lodash';
import PropTypes from 'prop-types';

import Config from 'Config';
import EditPhotoRequirementsModal from 'admin/components/work-orders/photoRequirements/EditPhotoRequirementsModal';
import { isMotiliAccount } from 'common/services/AuthService';
import UnmetRequirementsModal from 'admin/components/work-orders/photoRequirements/UnmetRequirementsModal';
import MotiliTextButton from '../../buttons/MotiliTextButton';

export default class DocumentRequirementsPhotosRequired extends Component {
    constructor(props) {
        super(props);
        this.state = {
            displayEditPhotoRequirementsModal: false,
            displayUnmetRequirementsModal: false,
        };
    }

    handleCloseModal = () => {
        this.setState({ displayEditPhotoRequirementsModal: false });
    };

    handleOpenModal = () => {
        this.setState({ displayEditPhotoRequirementsModal: true });
    };

    handleCloseUnmetRequirementsModal = () => {
        this.setState({ displayUnmetRequirementsModal: false });
    };

    handleOpenUnmetRequirementsModal = () => {
        this.setState({ displayUnmetRequirementsModal: true });
    };

    isAllowedToAlterRequirements = () => {
        const isWorkOrder = this.props.documentTypeId === 'WORK_ORDER';
        return (
            isMotiliAccount() &&
            isWorkOrder &&
            this.isWhitelistedDocumentStatus()
        );
    };

    displayUnmetRequirementsNumber = (
        unmetRequirements,
        isAllowedToAlterRequirements
    ) => {
        const photoTypeRequirements = this.props.photoTypeRequirements || [];
        if (!photoTypeRequirements.length) return null;

        const { red, green } = Config.color;
        const text = unmetRequirements
            ? `${unmetRequirements} not met`
            : 'all completed';

        return (
            <>
                {unmetRequirements &&
                isAllowedToAlterRequirements &&
                isAllowedToAlterRequirements() ? (
                    <MotiliTextButton
                        text='&nbsp;EDIT'
                        onClick={this.handleOpenModal}
                    />
                ) : null}
                <span style={{ color: unmetRequirements ? red : green }}>
                    &nbsp;
                    <em>{text}</em>
                </span>
            </>
        );
    };

    displayPhotoRequirements = () => {
        const photoTypeRequirements = sortBy(
            this.props.photoTypeRequirements || [],
            photoRequirement => {
                const photoTypeId = photoRequirement?.requirement?.id;
                if (!photoTypeId || this.props.photoTypes.length === 0) {
                    return Number.MAX_VALUE;
                }
                const photoType =
                    this.props.photoTypes[
                        findIndex(
                            this.props.photoTypes,
                            p => p.id === photoTypeId
                        )
                    ];
                if (!photoType || !photoType.displayOrder) {
                    return Number.MAX_VALUE;
                }
                return photoType.displayOrder;
            }
        );

        return photoTypeRequirements.length === 0
            ? this.addPhotoRequirementsActionDisplay()
            : photoTypeRequirements.map(this.photoRequirementDisplay);
    };

    isWhitelistedDocumentStatus = () => {
        const { documentStatusId } = this.props;
        const whitelistedStatuses = [
            'CREATED',
            'SCHEDULING',
            'SCHEDULED',
            'CONTACT_REQUIRED',
            'DISPATCHING',
            'DISPATCHED',
            'NO_CONTRACTOR_FOUND',
            'DELAYED',
            'IN_PROCESS',
            'DRAFTED',
            // 'ORDERED', not sure (no)
        ];
        return whitelistedStatuses.includes(documentStatusId);
    };

    addPhotoRequirementsActionDisplay = () => {
        return (
            <div>
                {'<NONE>'}
                {this.isAllowedToAlterRequirements() && (
                    <div style={{ marginTop: '5px' }}>
                        CLICK{' '}
                        <MotiliTextButton
                            text='HERE'
                            onClick={this.handleOpenModal}
                        />{' '}
                        TO ADD
                    </div>
                )}
            </div>
        );
    };

    photoRequirementDisplay = requirement => {
        const photoTypes = this.props.photoTypes || [];

        let requirementText =
            _find(photoTypes, { id: requirement.requirement.id })?.display ||
            requirement.display;
        requirementText += requirement.clientId ? ' (client)' : ' (project)';

        return (
            <div key={requirement.requirement.id}>
                {requirementText}
                {this.displayMetPhotoRequirementCheckmark(requirement)}
            </div>
        );
    };

    displayMetPhotoRequirementCheckmark = requirement => {
        return (
            this.photoRequirementMet(requirement.requirement.id) && (
                <>
                    &nbsp;
                    <i
                        style={{ color: Config.color.blue }}
                        className='fa fa-check'
                    />
                </>
            )
        );
    };

    /**
     * returns boolean true if photoType requirement is met
     * @param {*} photoType
     * @returns {boolean}
     */
    photoRequirementMet(photoType) {
        const photos = this.props.photos || [];

        const foundPhoto = photos.find(photo => {
            return photo.requirement.photoTypeId === photoType;
        });

        return !!foundPhoto;
    }

    /**
     * returns number of unmet photo requirements
     * @returns {number}
     */
    numberUnmetRequirements = () => {
        const photos = this.props.photos || [];
        const photoTypeRequirements = this.props.photoTypeRequirements || [];

        const byNameId = (photo, photoTypeRequirement) =>
            photo.requirement.photoTypeId ===
            photoTypeRequirement.requirement.id;
        const countTotalUnmet = (total, photoTypeRequirement) => {
            const foundPhoto = photos.find(photo => {
                return byNameId(photo, photoTypeRequirement);
            });
            return foundPhoto ? total : total + 1;
        };
        const unmetNumber = photoTypeRequirements.reduce(countTotalUnmet, 0);

        return unmetNumber;
    };

    render() {
        const photos = this.props.photos || [];
        const photoTypeRequirements = this.props.photoTypeRequirements || [];
        const {
            displayEditPhotoRequirementsModal,
            displayUnmetRequirementsModal,
        } = this.state;
        const unmetRequirements = this.numberUnmetRequirements();
        return (
            <>
                <FormLabel label='REQUIREMENTS' />
                {this.displayUnmetRequirementsNumber(
                    unmetRequirements,
                    this.isAllowedToAlterRequirements
                )}
                {this.props.photoTypeRequirements &&
                    this.props.photoTypeRequirements.length > 0 && (
                        <div
                            style={{
                                fontFamily: 'NunitoSans-Bold',
                                color: '#57c1e9',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                            }}
                            onClick={this.handleOpenUnmetRequirementsModal}
                        >
                            VIEW
                        </div>
                    )}
                {displayEditPhotoRequirementsModal && (
                    <EditPhotoRequirementsModal
                        photos={photos}
                        photoTypeRequirements={photoTypeRequirements}
                        handleClose={this.handleCloseModal}
                        handleEditPhotoRequirements={
                            this.props.handleEditPhotoRequirements
                        }
                    />
                )}
                {displayUnmetRequirementsModal && (
                    <UnmetRequirementsModal
                        unmetRequirementsList={this.displayPhotoRequirements()}
                        UnmetRequirementsHeader={() =>
                            this.displayUnmetRequirementsNumber(
                                unmetRequirements
                            )
                        }
                        handleClose={this.handleCloseUnmetRequirementsModal}
                    />
                )}
            </>
        );
    }
}

DocumentRequirementsPhotosRequired.propTypes = {
    documentStatusId: PropTypes.string,
    documentTypeId: PropTypes.string,
    photos: PropTypes.arrayOf(PropTypes.object),
    photoTypes: PropTypes.arrayOf(PropTypes.object),
    photoTypeRequirements: PropTypes.arrayOf(PropTypes.object),
};

DocumentRequirementsPhotosRequired.defaultProps = {
    documentStatusId: '',
    documentTypeId: '',
    photos: [],
    photoTypes: [],
    photoTypeRequirements: [],
};
